import { HIRE_ROUTES } from "plg/routes";
import type { NavigateFunction } from "react-router-dom";
import type { ApiResult } from "util/apiError";
import { fetchPlgMe } from "plg/api/plgAuth";
import { setPlgTokens } from "plg/auth/plgTokenStore";
import { saveSignupSession, signupTokenClaims } from "plg/utils/signupSession";
import { asSignupMethod, trackPlg, trackSignupBlocked } from "plg/analytics/events";
import { endPortalSession } from "plg/auth/portalSession";
import { clearSignupBlocked, rememberSignupBlocked } from "plg/utils/signupBlocked";
import { getNextStepPath } from "plg/utils/configSchema";
import type { PlgAuthStartResponse, PlgSignupConfig } from "plg/types";
import { PLG_SIGNUP_ROUTE } from "constants/routes";

export type SignInError =
  | "exists"
  | "adminExists"
  | "converted"
  | "suspended"
  | "disposable"
  | "unverified"
  | "country"
  | "blocked"
  | "magicInvalid"
  | "human"
  | "ipLimit"
  | "plusEmail"
  | "generic";

export const ERROR_BY_CODE: Record<string, SignInError> = {
  ACCOUNT_EXISTS: "exists",
  // An Edge admin: told they have a portal account, with no RM line (product 2026-10-29).
  ADMIN_ACCOUNT_EXISTS: "adminExists",
  ACCOUNT_CONVERTED: "converted",
  ACCOUNT_SUSPENDED: "suspended",
  DISPOSABLE_EMAIL: "disposable",
  INVALID_IDENTITY: "unverified",
  IDENTITY_NOT_VERIFIED: "unverified",
  COUNTRY_NOT_SUPPORTED: "country",
  MAGIC_LINK_INVALID: "magicInvalid",
  HUMAN_CHECK_REQUIRED: "human",
  SIGNUP_IP_LIMIT: "ipLimit",
  PLUS_EMAIL_NOT_ALLOWED: "plusEmail",
  ACCOUNT_BLOCKED: "blocked",
  // QA ON-1: a new public-mailbox sign-up, stopped right after sign-in (same card as ACCOUNT_BLOCKED).
  BUSINESS_EMAIL_REQUIRED: "blocked",
};

/** Why the PLG app sent the user back here (plgApi ends the session with ?reason=…). */
export const REASON_ERROR: Record<string, SignInError> = {
  suspended: "suspended",
  converted: "converted",
  country: "country",
  blocked: "blocked",
};

/**
 * One place for what an auth/start-shaped result means (Google, Microsoft, magic-link verify):
 * login → store PLG tokens and open /hire/home; signup → save the session and open the first step.
 * Returns the error to show, or null once it has navigated.
 */
export function applyAuthStartResult(
  res: ApiResult<PlgAuthStartResponse>,
  navigate: NavigateFunction,
  config: PlgSignupConfig | null,
  options: { replace?: boolean } = {}
): SignInError | null {
  if (res.ok === false) {
    trackSignupBlocked(res.code);
    const error = ERROR_BY_CODE[res.code || ""] || "generic";
    if (error === "blocked") {
      // A PLG identity that is blocked: no portal login stays behind (ON-6), and the block is remembered (ON-12).
      endPortalSession();
      rememberSignupBlocked();
    }
    return error;
  }
  endPortalSession();
  clearSignupBlocked();
  if (res.data.mode === "login") {
    if (!setPlgTokens({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken })) return "generic";
    fetchPlgMe().catch(() => undefined);
    navigate(HIRE_ROUTES.home, { replace: true });
    return null;
  }
  const method = asSignupMethod(signupTokenClaims(res.data.signupToken).provider);
  if (method) trackPlg("signup_started", { method });
  saveSignupSession({ signupToken: res.data.signupToken, prefill: res.data.prefill, enriched: res.data.enriched, answers: {} });
  const next = config ? getNextStepPath(config) : `${PLG_SIGNUP_ROUTE}/company`;
  if (options.replace) navigate(next, { replace: true });
  else navigate(next);
  return null;
}

/** A failed public GET (auth-config) — PlgHttpError carries the portal error code. */
export function errorFromLoadFailure(err: unknown): SignInError {
  return (err as { code?: unknown } | null)?.code === "COUNTRY_NOT_SUPPORTED" ? "country" : "generic";
}
